import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { map, catchError, tap, filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// Interfaces para tipado
export interface Compania {
  id: number;
  nombre: string;
  direccion?: string;
  comuna?: string;
  region?: string;
  telefono?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface RespuestaCompania {
  success: boolean;
  compania: Compania;
}

export interface RespuestaCompanias {
  success: boolean;
  count: number;
  companias: Compania[];
}

@Injectable({
  providedIn: 'root'
})
export class CompaniaService {
  // Nota: en tu proyecto environment.apiUrl ya suele incluir /api (p.ej. https://api.3dev.cl/api)
  // Por eso aquí NO agregamos otro /api.
  private baseUrl = `${environment.apiUrl}/companias`;
  
  // Cache para las compañías
  private companiasCache = new BehaviorSubject<Compania[]>([]);
  private companiaCache = new Map<number, BehaviorSubject<Compania | null>>();
  private cacheTimestamps = new Map<string, Date>();
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutos

  constructor(private http: HttpClient) {}

  /**
   * Obtiene los datos de una compañía específica
   * La API responde: { success: boolean, compania: {...} }
   */
  getCompania(companiaId: number, forceReload = false): Observable<Compania> {
    const cacheKey = `compania_${companiaId}`;
    
    if (!this.companiaCache.has(companiaId)) {
      this.companiaCache.set(companiaId, new BehaviorSubject<Compania | null>(null));
    }

    const cacheSubject = this.companiaCache.get(companiaId)!;
    const timestamp = this.cacheTimestamps.get(cacheKey);
    const ahora = new Date();

    // Si necesitamos recargar o el cache está vencido
    if (forceReload || !timestamp || (ahora.getTime() - timestamp.getTime() > this.CACHE_DURATION)) {
      this.cargarCompaniaFresh(companiaId);
    }

    // Solo retornar valores válidos, filtrar nulls
    return cacheSubject.asObservable().pipe(
      filter(compania => compania !== null),
      map(compania => compania as Compania)
    );
  }

  /**
   * Obtiene todas las compañías activas
   * La API responde: { success, count, companias: [...] }
   */
  getCompanias(forceReload = false): Observable<Compania[]> {
    const cacheKey = 'todas_companias';
    const timestamp = this.cacheTimestamps.get(cacheKey);
    const ahora = new Date();

    if (forceReload || !timestamp || (ahora.getTime() - timestamp.getTime() > this.CACHE_DURATION)) {
      this.cargarCompaniasFresh();
    }

    return this.companiasCache.asObservable();
  }

  /**
   * Obtiene compañías con filtros específicos
   * La API responde: { success, count, companias: [...] }
   */
  getCompaniasConFiltros(filtros: {
    limit?: number;
    offset?: number;
  } = {}): Observable<Compania[]> {
    const params = new URLSearchParams();

    if (filtros.limit) params.append('limit', filtros.limit.toString());
    if (filtros.offset) params.append('offset', filtros.offset.toString());

    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    return this.http.get<RespuestaCompanias>(url).pipe(
      map(r => r?.companias ?? []),
      catchError(this.handleError)
    );
  }

  /**
   * Crea una nueva compañía
   * Asumiendo que tu API retorna directamente la compañía creada
   */
  crearCompania(compania: Partial<Compania>): Observable<Compania> {
    return this.http.post<Compania>(this.baseUrl, compania).pipe(
      tap(() => {
        // Invalidar cache al crear
        this.invalidarCache();
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Actualiza una compañía existente
   * Asumiendo que tu API retorna directamente la compañía actualizada
   */
  actualizarCompania(id: number, datos: Partial<Compania>): Observable<Compania> {
    return this.http.put<Compania>(`${this.baseUrl}/${id}`, datos).pipe(
      tap((compania) => {
        // Actualizar cache local
        this.actualizarCacheCompania(compania);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Elimina una compañía (soft delete)
   */
  eliminarCompania(id: number): Observable<{ success: boolean, message: string }> {
    return this.http.delete<{ success: boolean, message: string }>(`${this.baseUrl}/${id}`).pipe(
      tap(() => {
        // Invalidar cache al eliminar
        this.invalidarCache();
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Fuerza la actualización del cache
   */
  public forzarRefresco(companiaId?: number): void {
    if (companiaId) {
      this.invalidarCacheCompania(companiaId);
      this.cargarCompaniaFresh(companiaId);
    } else {
      this.invalidarCache();
      this.cargarCompaniasFresh();
    }
  }

  /**
   * Obtiene el nombre completo de una compañía (útil para templates)
   */
  getNombreCompania(companiaId: number): Observable<string> {
    return this.getCompania(companiaId).pipe(
      map((compania: Compania) => compania.nombre),
      catchError(() => {
        // Fallback en caso de error
        return new Observable<string>(subscriber => {
          subscriber.next(`${companiaId}ª Compañía`);
          subscriber.complete();
        });
      })
    );
  }

  // ===== MÉTODOS PRIVADOS =====

  private cargarCompaniaFresh(companiaId: number): void {
    // Tu API individual retorna { compania: {...}, success: true }
    this.http.get<RespuestaCompania>(`${this.baseUrl}/${companiaId}`).pipe(
      map((response: RespuestaCompania) => response?.compania as Compania),
      catchError(this.handleError)
    ).subscribe({
      next: (compania) => {
        const cacheSubject = this.companiaCache.get(companiaId);
        if (cacheSubject) {
          cacheSubject.next(compania);
          this.cacheTimestamps.set(`compania_${companiaId}`, new Date());
        }
      },
      error: (err) => {
        console.error(`Error al cargar compañía ${companiaId}:`, err);
        const cacheSubject = this.companiaCache.get(companiaId);
        if (cacheSubject) {
          cacheSubject.error(err);
        }
      }
    });
  }

  private cargarCompaniasFresh(): void {
    // Tu API lista retorna { success, count, companias: [...] }
    this.http.get<RespuestaCompanias>(this.baseUrl).pipe(
      catchError(this.handleError)
    ).subscribe({
      next: (resp) => {
        const companias = resp?.companias ?? [];
        this.companiasCache.next(companias);
        this.cacheTimestamps.set('todas_companias', new Date());
        
        // También actualizar cache individual de cada compañía
        companias.forEach(compania => {
          const cacheSubject = this.companiaCache.get(compania.id);
          if (cacheSubject) {
            cacheSubject.next(compania);
            this.cacheTimestamps.set(`compania_${compania.id}`, new Date());
          }
        });
      },
      error: (err) => {
        console.error('Error al cargar compañías:', err);
      }
    });
  }

  private actualizarCacheCompania(compania: Compania): void {
    // Actualizar cache individual
    const cacheSubject = this.companiaCache.get(compania.id);
    if (cacheSubject) {
      cacheSubject.next(compania);
      this.cacheTimestamps.set(`compania_${compania.id}`, new Date());
    }

    // Actualizar en el cache de todas las compañías
    const companiasActuales = this.companiasCache.value;
    const indice = companiasActuales.findIndex(c => c.id === compania.id);
    if (indice >= 0) {
      const nuevasCompanias = [...companiasActuales];
      nuevasCompanias[indice] = compania;
      this.companiasCache.next(nuevasCompanias);
    }
  }

  private invalidarCache(): void {
    this.cacheTimestamps.clear();
  }

  private invalidarCacheCompania(companiaId: number): void {
    this.cacheTimestamps.delete(`compania_${companiaId}`);
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    console.error('Error en CompaniaService:', error);
    
    let errorMessage = 'Error desconocido';
    
    if (error.error?.error) {
      errorMessage = error.error.error;
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.status === 0) {
      errorMessage = 'Error de conexión. Verifique su conexión a internet.';
    } else if (error.status === 404) {
      errorMessage = 'Compañía no encontrada.';
    } else if (error.status === 500) {
      errorMessage = 'Error interno del servidor.';
    }

    return throwError(() => ({
      ...error,
      message: errorMessage
    }));
  };

  // ===== MÉTODOS UTILITARIOS =====

  /**
   * Extrae el mensaje de error de una respuesta HTTP
   */
  extraerMensajeError(error: any): string {
    if (error?.error?.error) return error.error.error;
    if (error?.error?.message) return error.error.message;
    if (typeof error?.error === 'string') return error.error;
    if (error?.message) return error.message;
    return 'Error desconocido al cargar datos de la compañía';
  }

  /**
   * Obtiene un mensaje amigable basado en el código de error
   */
  obtenerMensajeAmigable(codigo: string): string {
    const mensajes: { [key: string]: string } = {
      'COMPANIA_NO_ENCONTRADA': 'La compañía solicitada no existe.',
      'COMPANIA_INACTIVA': 'La compañía está marcada como inactiva.',
      'NOMBRE_DUPLICADO': 'Ya existe una compañía con ese nombre.',
      'CAMPOS_REQUERIDOS': 'Faltan campos requeridos para crear la compañía.',
      'ERROR_CONEXION': 'Error de conexión con el servidor.',
      'SIN_PERMISOS': 'No tiene permisos para realizar esta operación.',
      'ERROR_INTERNO': 'Error interno del servidor. Intente nuevamente.'
    };

    return mensajes[codigo] || 'Error desconocido.';
  }

  /**
   * Valida si una compañía es válida para operaciones
   */
  validarCompania(compania: Partial<Compania>): { valida: boolean; errores: string[] } {
    const errores: string[] = [];

    if (!compania.nombre?.trim()) {
      errores.push('El nombre es requerido');
    }

    if (compania.nombre && compania.nombre.length > 255) {
      errores.push('El nombre no puede exceder 255 caracteres');
    }

    return {
      valida: errores.length === 0,
      errores
    };
  }
}
